import { stripLocalePrefix } from '~/utils/i18n/locale';

/**
 * Post-auth `?redirect=` rules shared by `useRedirectAfterAuth` (building the link + resolving it
 * after login/register) and the `is-auth-route` middleware (already-logged-in visitors).
 *
 * Public sales/landing pages are never redirect targets: sending someone who just signed up (or is
 * already logged in) back to the page that pitched the signup is a dead end. Content pages the
 * visitor actually wanted (explore, projects, developers, properties, services…) stay valid.
 */

/** Exact (locale-stripped) landing paths. */
const LANDING_EXACT_PATHS: ReadonlySet<string> = new Set([
  '/',
  '/uae',
  '/international',
  '/agents',
  '/saas',
  '/contact',
  '/masterclass',
]);

/** Landing path prefixes: everything below them is a landing page. */
const LANDING_PREFIXES = ['/lp/'] as const;

/** `/masterclass/<segment>` routes that are NOT sales variants (portal / auth screens). */
const MASTERCLASS_NON_LANDING_SEGMENTS: ReadonlySet<string> = new Set(['learn', 'onboarding', 'login']);

const MASTERCLASS_PREFIX = '/masterclass/';

const pathOnly = (value: string): string => {
  const cut = value.search(/[?#]/);
  const path = cut === -1 ? value : value.slice(0, cut);
  // Treat `/agents/` like `/agents` (but keep `/` itself).
  return path.length > 1 ? path.replace(/\/+$/, '') || '/' : path;
};

const isMasterclassVariantPath = (path: string): boolean => {
  if (!path.startsWith(MASTERCLASS_PREFIX)) return false;
  const rest = path.slice(MASTERCLASS_PREFIX.length);
  // A variant is a single segment (`/masterclass/v2-actor`, `/masterclass/start`).
  if (rest.length === 0 || rest.includes('/')) return false;
  return !MASTERCLASS_NON_LANDING_SEGMENTS.has(rest);
};

/** True for public marketing/sales landing pages (any locale prefix, query and hash ignored). */
export const isMarketingLandingPath = (value: string): boolean => {
  const path = pathOnly(stripLocalePrefix(value));
  if (LANDING_EXACT_PATHS.has(path)) return true;
  if (LANDING_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  return isMasterclassVariantPath(path);
};

/** Same-origin, root-relative, not an auth screen. */
export const isSafeInternalPath = (value: unknown): value is string => {
  if (typeof value !== 'string' || value.length === 0) return false;
  // Reject protocol-relative (`//evil.com`, `/\evil.com`) and absolute external URLs.
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return false;
  // Don't loop guests back into auth screens.
  return !value.startsWith('/auth/');
};

/** A `?redirect=` value we will actually navigate to after auth. */
export const isValidPostAuthRedirect = (value: unknown): value is string =>
  isSafeInternalPath(value) && !isMarketingLandingPath(value);
